import { useEffect, useRef, useState } from "react";
import "./sessionCountdownSlot.css";

/**
 * GMA-1909 — reserved space for the ONE countdown, without duplicating it.
 *
 * Placement went through three wrong shapes before this one: in each shell's
 * header (missed WebShell, /accept-terms and /call), then a fixed badge
 * (invisible under the fixed header), then a fixed badge raised above the
 * header (which covered Log Out and the notification bell).
 *
 * So: a shell RESERVES space by rendering a slot; the single badge portals into
 * it when one exists and falls back to fixed positioning where none does. One
 * instance, no overlay of interactive chrome, and a surface without a shell
 * still gets the countdown.
 */

let slotEl = null;
const subscribers = new Set();

const publish = () => subscribers.forEach((fn) => fn(slotEl));

/** Called by the slot on mount; returns its own teardown. */
export const registerSlot = (el) => {
  slotEl = el;
  publish();
  return () => {
    if (slotEl === el) {
      slotEl = null;
      publish();
    }
  };
};

/** null until a shell has reserved space. */
export const useSessionCountdownSlot = () => {
  const [el, setEl] = useState(slotEl);
  useEffect(() => {
    subscribers.add(setEl);
    setEl(slotEl);
    return () => subscribers.delete(setEl);
  }, []);
  return el;
};

/** Rendered by each authenticated shell, ahead of its own controls. */
export const SessionCountdownSlot = () => {
  const ref = useRef(null);
  useEffect(() => registerSlot(ref.current), []);
  return (
    <div
      ref={ref}
      className="session-countdown-slot"
      data-testid="session-countdown-slot"
    />
  );
};

export default SessionCountdownSlot;
